/** The workspace's right column: who wrote in, their memberships, payments, earlier conversations and reading. */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { fmtDate } from '../../lib/format'
import { TITLE_LABELS, stateLabel, topicLabel, type DeskChannel, type MemberContext } from '../../lib/supportDesk'
import { centsLabel, earlierHref, paymentsKeptElsewhere, paymentsKeptHere, sourceLabel } from '../../lib/supportWorkspace'
import { StaffViewButton } from '../StaffViewButton'

export const NOT_KEPT_LINE = "Payments for PayPal and SimpleCirc memberships aren't kept in AAMCirc"

const MATCH_TEXT: Record<string, string> = {
  ambiguous: 'More than one member uses this address, so no member details are shown.',
  none: 'No member record matches this sender.',
  account_number_unconfirmed: 'The account number they gave does not match their address.',
}

const STATUS_LABEL: Record<string, string> = { archived: stateLabel('done'), read: 'Reviewed', new: stateLabel('to_review') }

type Props = { channel: DeskChannel; id: number }

export function MemberPanel({ channel, id }: Props) {
  const ctx = useQuery<MemberContext>({
    queryKey: ['support', 'member-context', channel, id],
    queryFn: () => api.get<MemberContext>(`/api/support/member-context?channel=${channel}&id=${id}`),
    retry: false,
  })

  if (ctx.isLoading) return <Shell><p className="text-paper-trace text-sm">Loading member…</p></Shell>
  if (ctx.isError || !ctx.data) return <Shell><p className="text-red-400 text-sm">Couldn't load member details.</p></Shell>
  const m = ctx.data
  // Payments only for memberships AAMCirc bills; the not-kept line only for PayPal and SimpleCirc.
  // Manual, offline and gift memberships show neither.
  const kept = m.memberships.some(s => paymentsKeptHere(s.source))
  const notKept = m.memberships.some(s => paymentsKeptElsewhere(s.source))

  return (
    <Shell>
      {m.writer_unverified && (
        <p role="alert" className="border border-red-400 text-red-300 px-3 py-2 text-sm font-semibold">
          Sender not verified — don't share account details
        </p>
      )}

      <Block label="Member">
        {m.person ? (
          <>
            <div className="text-paper text-lg">{m.person.name ?? 'No name on file'}</div>
            <div className="font-mono text-xs text-paper-dim break-all">
              {m.person.account_number ? `#${m.person.account_number} · ` : ''}{m.person.email ?? 'no email'}
            </div>
            <Link to={`/subscribers/${m.person.subscriber_id}`} className="label-amber hover:underline">Open member record →</Link>
            <div className="mt-2"><StaffViewButton subscriberId={m.person.subscriber_id} /></div>
          </>
        ) : (
          <p className="text-paper-dim text-sm">{MATCH_TEXT[m.match ?? 'none'] ?? MATCH_TEXT.none}</p>
        )}
      </Block>

      {m.memberships.map((s, i) => (
        <Block key={`${s.title}-${i}`} label={TITLE_LABELS[s.title] ?? s.title}>
          <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 text-sm">
            <Row k="Status" v={s.status} />
            <Row k="Plan" v={s.plan ?? '—'} />
            <Row k="Renews" v={fmtDate(s.current_period_end)} />
            <Row k="Auto-renew" v={s.auto_renew ? 'On' : 'Off'} />
            <Row k="Next renewal" v={centsLabel(s.next_renewal_cents)} />
            <Row k="Card" v={s.card ? `${s.card.brand} •• ${s.card.last4}` : '—'} />
            <Row k="Member since" v={fmtDate(s.member_since)} />
            <Row k="Billed by" v={sourceLabel(s.source, s.instrument)} />
          </dl>
        </Block>
      ))}

      {m.person && (kept || notKept) && (
        <Block label="Recent payments">
          {kept && (m.payments.length === 0
            ? <p className="text-paper-trace text-sm">No payments recorded.</p>
            : (
              <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 text-sm">
                {m.payments.map((p, i) => (
                  <Row key={i} k={`${fmtDate(p.at)} · ${TITLE_LABELS[p.title] ?? p.title}`}
                    v={`${p.kind === 'charge' ? '' : `${p.kind} `}${centsLabel(p.amount_cents)}${p.status ? ` · ${p.status}` : ''}`} />
                ))}
              </dl>
            ))}
          {notKept && <p className="text-paper-dim text-sm">{NOT_KEPT_LINE}</p>}
        </Block>
      )}

      <Block label="Earlier conversations">
        {m.earlier.length === 0 && <p className="text-paper-trace text-sm">None.</p>}
        {m.earlier.map(e => (
          <Link key={`${e.channel}-${e.id}`} to={earlierHref(e)}
            className="block text-sm text-amber-glow hover:underline">
            {fmtDate(e.at, { month: 'short', year: 'numeric' })} · {topicLabel(e.topic)} · {STATUS_LABEL[e.status] ?? e.status}
          </Link>
        ))}
      </Block>

      {m.reading && (
        <Block label="Reading">
          <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 text-sm">
            <Row k="Reading days" v={m.reading.reading_days ?? '—'} />
            <Row k="Engaged minutes" v={m.reading.engaged_minutes ?? '—'} />
            <Row k="Engagement score" v={m.reading.score ?? '—'} />
          </dl>
        </Block>
      )}
    </Shell>
  )
}

function Shell({ children }: { children: ReactNode }) {
  return <aside aria-label="Member" className="flex flex-col gap-5 p-4 border-ink-500 xl:border-l bg-ink-800 min-w-0">{children}</aside>
}

function Block({ label, children }: { label: string; children: ReactNode }) {
  return <div className="flex flex-col gap-1.5"><div className="label">{label}</div>{children}</div>
}

function Row({ k, v }: { k: string; v: ReactNode }) {
  return <><dt className="text-paper-dim">{k}</dt><dd className="text-right font-mono text-xs text-paper">{v}</dd></>
}
